import { useMemo } from "react";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFeedScrollMemory } from "../../domains/app/stores/scrollPositionsStore";
import { type PracticeTabSelection } from "../../components/practice-feed/PracticeFeedRail";
import type { PracticeFeedFilters } from "../../components/practice-feed/MeshFilterDrawer";
import { usePracticeFeed } from "../../domains/practice/hooks/usePracticeFeed";
import {
  usePracticeRailTerms,
  type PracticeRailCorpus,
} from "../../domains/practice/hooks/usePracticeRailTerms";
import { getItemTranslation } from "../../domains/app/utils/getItemTranslation";
import { useApiQuery } from "../../services/api";
import { useGetForYouRecommendedContentQuery } from "../../domains/forYou/hooks/useGetForYouRecommendedContentQuery";
import { useFeedSeed } from "../../domains/forYou/hooks/useFeedSeed";
import { useEffectOnce } from "../../domains/app/hooks/useEffectOnce";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { getRoomName } from "../../domains/room/hooks/useRoomName";
import { roomHasNewContent } from "../../domains/room/utils/roomVisits";
import { AnalyticsItemList } from "../../tools/analytics/analytics.types";
import { OrganisationUid } from "../../interfaces";
import { gaEventViewItemListMixedContent } from "../../tools/analytics/domains/content.analytics";
import {
  type PracticeFeedView,
  WHOLE_PRACTICE,
  buildSelectionSearch,
  litChip,
  modeForChip,
  readSelectionFromSearch,
  resolvePracticeSelection,
} from "./practiceFeedSelection";

/**
 * Everything the practice feed page reads and does, shared by the mobile and
 * the desktop layout, including the feed's scroll container (`scrollRef`,
 * attached by the page to the element that scrolls, with `rememberScroll` as
 * its `onScroll`): the feed comes back where it was left, and switching chip
 * or practice starts the destination at the top.
 */
export function usePracticeFeedPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { activeLang } = useActiveLang();
  /**
   * Whether a card names the rail term it carries.
   *
   * The CARD chip only. The rail keeps its chips either way, and that separation
   * is the whole reason the flag says `CARD`: the two are the same vocabulary
   * drawn twice, and only the second one is in question. Turning this off leaves
   * the reader every way of filtering they had.
   *
   * Read reactively in the body, like every other flag here: flags answer `false`
   * until the GrowthBook payload lands
   * (`.knowledge/growthbook-oneshot-flag-race.md`), so a value captured once at
   * mount would show no chip for the whole session on a slow payload. Re-reading
   * means the chips appear a frame late instead of never.
   *
   * `false` before the payload is also the SAFE direction here, which is why this
   * needs no third state the way `usePracticeArm` does: a card with no chip is a
   * card, and it is exactly what the empty-rail path already renders.
   */
  const WEBAPP_PRACTICE_CARD_MESH_CHIP = useFeatureIsOn(
    "WEBAPP_PRACTICE_CARD_MESH_CHIP"
  );

  const joinedRoomsQuery = useApiQuery("/webapp/private/rooms", {});
  // The chips ARE the reader's catalogue. No cohort is consulted: the router
  // already resolved the arm before this page was mounted, and re-deciding it
  // here could disagree with it.
  const practicesQuery = useApiQuery("/webapp/private/practice-profiles", {});
  const practices =
    practicesQuery.state === "success"
      ? practicesQuery.body.docs.map((practice) => ({
          uid: practice.uid,
          name: getItemTranslation(practice),
        }))
      : [];
  // Derived from the catalogue, not merely parsed from the URL — see
  // `resolvePracticeSelection`. It has to sit after the catalogue read, which is
  // why the room id below is read off the resolved value rather than the raw one.
  const view = resolvePracticeSelection({
    requested: readSelectionFromSearch(location.search),
    catalogue: practicesQuery.state === "success" ? practices : null,
  });
  const mode = view === null ? null : view.mode;
  const selection = mode === null ? null : litChip(mode);
  const selectedRoomId =
    mode !== null && mode.kind === "room" ? mode.roomId : null;
  const selectedDescriptorUi =
    mode !== null && mode.kind === "practice" ? mode.descriptorUi : null;
  // The drawer's two axes, read once. Room mode carries none by construction, so
  // this is the empty pair there rather than a state anything has to guard.
  const filters: PracticeFeedFilters =
    mode !== null && mode.kind === "practice"
      ? {
          journalUids: mode.journalUids,
          publicationTypes: mode.publicationTypes,
        }
      : { journalUids: [], publicationTypes: [] };
  // The corpus, named in the header dropdown and in the feed's screen-reader
  // heading. `find` can miss while a `?practice=` deep link waits for the
  // catalogue, which reads as no name rather than an empty one.
  // Bound to a local before the lookup: TypeScript drops a narrowing made on a
  // PROPERTY the moment it is read inside a closure, so `view.corpus.uid` in the
  // `find` below would not be narrowed. The local is.
  const viewCorpus = view === null ? null : view.corpus;
  const selectedPractice =
    viewCorpus === null || viewCorpus.kind === "all"
      ? undefined
      : practices.find((entry) => entry.uid === viewCorpus.uid);
  const selectedPracticeName =
    viewCorpus !== null && viewCorpus.kind === "all"
      ? t("All practices")
      : selectedPractice === undefined
        ? null
        : selectedPractice.name;
  const newContentQuery = useApiQuery("/webapp/private/rooms/new-content", {});
  const newContentByRoomId =
    newContentQuery.state === "success" ? newContentQuery.body : {};
  const rooms =
    joinedRoomsQuery.state === "success"
      ? joinedRoomsQuery.body.rooms
          .map((room) => ({
            id: room.id,
            name: getRoomName(room, activeLang),
            badge: roomHasNewContent(
              newContentByRoomId[room.id] ?? null,
              room.id
            ),
          }))
          .sort((a, b) => {
            if (a.badge === b.badge) return 0;
            return a.badge ? -1 : 1;
          })
      : [];
  const hasRooms = rooms.length > 0;

  const selectedRoom = useMemo(() => {
    if (selectedRoomId === null || joinedRoomsQuery.state !== "success")
      return null;
    return (
      joinedRoomsQuery.body.rooms.find((room) => room.id === selectedRoomId) ??
      null
    );
  }, [selectedRoomId, joinedRoomsQuery.state]);

  // Room mode still reads the ordinary feed — a Room's content is curated and
  // ranked, and this lot changes what "relevant to me" means, not what a Room is.
  const { seed, refresh } = useFeedSeed();
  const roomFeed = useGetForYouRecommendedContentQuery(
    selectedRoomId,
    null,
    seed
  );
  // The rail is a property of the PRACTICE and not of the filter: it does not
  // re-fetch when a chip is tapped, and it stays up in Room mode. A Room chip
  // lights beside the same MeSH chips, and those chips are the way back to the
  // practice: without them the row shrinks to the one lit Room and there is
  // nothing left to scroll to.
  //
  // Read BEFORE the feed because the feed now depends on it: the rail is what
  // decides whether a requested term is one this reader can see.
  const railTerms = usePracticeRailTerms(view === null ? null : view.corpus);

  /**
   * The term the feed actually narrows by, which is NOT always the one the URL
   * asked for.
   *
   * A URL can name a descriptor this rail does not carry — a link shared from
   * "All practices", where the rail is merged from whichever practices the
   * READER holds, is only valid for a reader holding the same ones. The feed
   * would filter by it while no chip lit and the drawer offered nothing to
   * clear: a filter in force with no way to see or remove it, which is worse
   * than no filter at all.
   *
   * So the rail arbitrates. While it is `loading` the URL is honoured — dropping
   * a term merely because the answer has not arrived would break every legitimate
   * deep link. Once it is `ready` and does not carry the term, the term is inert.
   */
  const selectedTermIsOnRail =
    selectedDescriptorUi === null ||
    railTerms.status !== "ready" ||
    railTerms.terms.some((term) => term.descriptorUi === selectedDescriptorUi);
  const effectiveDescriptorUi = selectedTermIsOnRail
    ? selectedDescriptorUi
    : null;
  // The lit chip as the rail labels it, for the end-of-feed card. `null` while
  // the rail still loads (the URL is honoured, the label is not known yet), so
  // the card waits rather than naming a bare descriptor UI.
  const litTerm =
    effectiveDescriptorUi === null || railTerms.status !== "ready"
      ? null
      : railTerms.terms.find(
          (term) => term.descriptorUi === effectiveDescriptorUi
        );
  if (litTerm === undefined)
    throw new Error(
      "Invariant: a descriptor the rail does not carry is inert (selectedTermIsOnRail)"
    );

  // Everything else reads the practice endpoint, one practice at a time, narrowed
  // to the rail's live chip when there is one.
  const practiceFeed = usePracticeFeed(
    view === null || selectedRoomId !== null
      ? null
      : {
          corpus: view.corpus,
          descriptorUi: effectiveDescriptorUi,
          journalUids: filters.journalUids,
          publicationTypes: filters.publicationTypes,
        }
  );

  // Read once: everything below wants "the articles on screen", and pending and
  // error both put none there.
  const practiceArticles =
    practiceFeed.state === "ready" ? practiceFeed.articles : [];
  // The card chip's raw material, served beside the docs by the same endpoint.
  const practiceDescriptorUis =
    practiceFeed.state === "ready"
      ? practiceFeed.majorDescriptorUisByArticleId
      : new Map<string, readonly string[]>();

  const practiceArticleIds = practiceArticles
    .map((article) => article.id)
    .join(",");
  useEffectOnce(() => {
    if (practiceArticles.length === 0) return;
    gaEventViewItemListMixedContent({
      content: practiceArticles,
      listName: AnalyticsItemList.PRACTICE_FEED,
      environmentParams: {
        type: "public" as const,
        partner: OrganisationUid.JUISCI,
        partner_id: null,
        room_id: null,
      },
    });
  }, [practiceArticleIds]);

  // Room mode keeps reporting exactly what it reported before, under the list
  // name it always used: the room surface is unchanged by this arm, and renaming
  // its list would break the comparison with the control page's room mode.
  const roomFeedContentIds = roomFeed.contentList
    .map((item) => item.id)
    .join(",");
  useEffectOnce(() => {
    if (roomFeed.contentList.length === 0) return;
    if (selectedRoom === null) return;
    gaEventViewItemListMixedContent({
      content: roomFeed.contentList,
      listName: AnalyticsItemList.FORYOU,
      environmentParams: {
        type: "professional" as const,
        partner: selectedRoom.organisationModel.uid,
        partner_id: selectedRoom.organisationModel.id,
        room_id: selectedRoom.id,
      },
    });
  }, [roomFeedContentIds]);

  const recommendedRoomsQuery = useApiQuery(
    "/webapp/private/rooms/recommended",
    {}
  );

  const topRecommendedRoom =
    recommendedRoomsQuery.state === "success"
      ? (recommendedRoomsQuery.body.rooms[0] ?? null)
      : null;

  const feedArticles = selectedRoomId !== null ? roomFeed.contentList : [];
  const hasContent =
    selectedRoomId !== null
      ? roomFeed.contentList.length > 0
      : practiceArticles.length > 0;
  const { scrollRef, rememberScroll, startAtTop } =
    useFeedScrollMemory<HTMLDivElement>(
      `/discovery${location.search}`,
      hasContent
    );

  function goTo(next: PracticeFeedView) {
    const nextSearch = buildSelectionSearch(next);
    // Compared against the URL ITSELF, not against the view re-encoded from it. A
    // query string this arm cannot serve (a `?topic=` bookmarked in the other one)
    // reads as nothing pinned, so re-encoding it would differ from `location.search`
    // and the stale parameter would be dropped on the first tap — which is what
    // should happen. Comparing re-encoded values would instead early-return and
    // leave it in the URL for good, with it a phantom `scrollPathname` key.
    if (nextSearch === location.search) return;
    startAtTop(`/discovery${nextSearch}`);
    history.push({ pathname: "/discovery", search: nextSearch });
  }

  // A chip. `null` is a re-tap on the live MeSH chip, or the term's cross in the
  // drawer. The practice is read off the resolved view and carried through
  // untouched — a chip never changes the corpus. What it does to the drawer's two
  // axes is `modeForChip`'s single rule.
  function handleSelectChip(next: PracticeTabSelection | null) {
    if (view === null) return;
    goTo({
      corpus: view.corpus,
      mode: modeForChip(view.mode, next),
    });
  }

  // A checkbox in the drawer. It leaves the chip alone: the axes narrow WITHIN
  // the live chip, they do not replace it, which is what makes the pinned counter
  // read 2 or 3 rather than flipping between them.
  function handleChangeFilters(next: PracticeFeedFilters) {
    if (view === null || view.mode.kind !== "practice") return;
    goTo({
      corpus: view.corpus,
      mode: {
        kind: "practice",
        descriptorUi: view.mode.descriptorUi,
        journalUids: next.journalUids,
        publicationTypes: next.publicationTypes,
      },
    });
  }

  // "Clear all". Every filter off, the practice untouched — the invariant the
  // drawer's footer states out loud.
  function handleClearAll() {
    if (view === null) return;
    goTo({ corpus: view.corpus, mode: WHOLE_PRACTICE });
  }

  // The header dropdown. Changing the corpus drops EVERY filter — the chip, both
  // axes, and a Room: all three were chosen from the practice that is leaving. It
  // is the symmetry of "Clear all never clears the practice" — the practice is
  // not a filter, so it does not accumulate with them.
  function handleSelectCorpus(corpus: PracticeRailCorpus) {
    goTo({ corpus, mode: WHOLE_PRACTICE });
  }

  function handleAddRoom() {
    history.push("/rooms");
  }

  function handleDiscoverRoom(roomId: string) {
    // Carry the channel so a join made from this card is attributable. Without
    // it the join defaulted to "public app" and was indistinguishable from any
    // other, which left the PRD's comparator without a numerator on the very
    // side it compares against.
    history.push(`/room/discovery/${roomId}`, {
      roomDiscoveryChannel: "feed card",
    });
  }

  return {
    scrollRef,
    rememberScroll,
    view,
    selection,
    selectedRoomId,
    selectedDescriptorUi,
    filters,
    selectedPractice,
    selectedPracticeName,
    practices,
    rooms,
    hasRooms,
    roomFeed,
    refresh,
    railTerms,
    effectiveDescriptorUi,
    litTerm,
    practiceFeed,
    practiceArticles,
    practiceDescriptorUis,
    topRecommendedRoom,
    feedArticles,
    WEBAPP_PRACTICE_CARD_MESH_CHIP,
    handleSelectChip,
    handleChangeFilters,
    handleClearAll,
    handleSelectCorpus,
    handleAddRoom,
    handleDiscoverRoom,
  };
}
